import { useEffect, useRef, useState } from "react";
import {
  TerraDraw,
  TerraDrawModeUndoRedo,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  TerraDrawSessionUndoRedo,
} from "terra-draw";
import {
  TERRA_DRAW_COORDINATE_PRECISION,
  latLngToTerraDrawPolygon,
  removePolygonVertexNear,
  terraDrawPolygonToLatLng,
} from "@/lib/branchCoveragePolygon";
import { TowerGoogleMapsAdapter } from "@/lib/TowerGoogleMapsAdapter";

type DrawingStatus =
  | "loading"
  | "ready"
  | "drawing"
  | "editing"
  | "finished"
  | "error";

interface BranchCoverageDrawControlProps {
  enabled: boolean;
  initialCoordinates: google.maps.LatLngLiteral[];
  map: google.maps.Map | null;
  onCancel: () => void;
  onPolygonChange: (coordinates: google.maps.LatLngLiteral[]) => void;
  onPolygonComplete: (coordinates: google.maps.LatLngLiteral[]) => void;
  onStart: () => void;
}

export default function BranchCoverageDrawControl({
  enabled,
  initialCoordinates,
  map,
  onCancel,
  onPolygonChange,
  onPolygonComplete,
  onStart,
}: BranchCoverageDrawControlProps) {
  const drawRef = useRef<TerraDraw | null>(null);
  const initialCoordinatesRef = useRef(initialCoordinates);
  const [status, setStatus] = useState<DrawingStatus>("loading");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    initialCoordinatesRef.current = initialCoordinates;
  }, [initialCoordinates]);

  useEffect(() => {
    if (!enabled || !map || !window.google?.maps) {
      setStatus("loading");
      return;
    }

    let draw: TerraDraw | null = null;
    let animationFrameId: number | null = null;
    let projectionListener: google.maps.MapsEventListener | null = null;
    let cameraListener: google.maps.MapsEventListener | null = null;
    let doubleClickListener: google.maps.MapsEventListener | null = null;
    let coverageFeatureId: string | number | undefined;
    let disposed = false;

    const initializeDraw = () => {
      animationFrameId = window.requestAnimationFrame(() => {
        if (disposed) return;

        try {
          draw = new TerraDraw({
            adapter: new TowerGoogleMapsAdapter({
              lib: window.google.maps,
              map,
              coordinatePrecision: TERRA_DRAW_COORDINATE_PRECISION,
              forwardMapElementEvents: true,
              isolatedData: true,
            }),
            modes: [
              new TerraDrawPolygonMode({
                editable: true,
                showCoordinatePoints: false,
                styles: {
                  fillColor: "#FF0000",
                  fillOpacity: 0.35,
                  outlineColor: "#FF0000",
                  outlineOpacity: 0.8,
                  outlineWidth: 2,
                },
              }),
              new TerraDrawSelectMode({
                allowManualDeselection: false,
                styles: {
                  midPointColor: "#FFFFFF",
                  midPointOpacity: 1,
                  midPointOutlineColor: "#059669",
                  midPointOutlineOpacity: 1,
                  midPointOutlineWidth: 2,
                  midPointWidth: 8,
                  selectedPointColor: "#059669",
                  selectedPointOpacity: 1,
                  selectedPointOutlineColor: "#FFFFFF",
                  selectedPointOutlineOpacity: 1,
                  selectedPointOutlineWidth: 2,
                  selectedPointWidth: 10,
                  selectedPolygonColor: "#FF0000",
                  selectedPolygonFillOpacity: 0.35,
                  selectedPolygonOutlineColor: "#FF0000",
                  selectedPolygonOutlineOpacity: 0.8,
                  selectedPolygonOutlineWidth: 2,
                },
                flags: {
                  polygon: {
                    feature: {
                      draggable: true,
                      coordinates: {
                        draggable: true,
                        deletable: true,
                        midpoints: { draggable: true },
                      },
                    },
                  },
                },
              }),
            ],
            undoRedo: {
              modeLevel: new TerraDrawModeUndoRedo({ maxStackSize: 100 }),
              sessionLevel: new TerraDrawSessionUndoRedo({ maxStackSize: 100 }),
            },
          });

          const refreshHistory = () => {
            if (!draw) return;
            setCanUndo(draw.canUndo());
            setCanRedo(draw.canRedo());
          };

          const getFeatureCoordinates = (id: string | number) => {
            if (!draw) return false;
            const coordinates = terraDrawPolygonToLatLng(
              draw.getSnapshotFeature(id),
            );
            if (!coordinates) return false;
            return coordinates;
          };

          const loadInitialFeature = () => {
            if (!draw) return;
            const feature = latLngToTerraDrawPolygon(
              initialCoordinatesRef.current,
              draw.getFeatureId(),
            );

            if (!feature) {
              setStatus("ready");
              return;
            }

            const [validation] = draw.addFeatures([feature]);
            if (!validation?.valid) {
              console.error(
                "No se pudo cargar el polígono de cobertura:",
                validation?.reason,
              );
              setStatus("error");
              return;
            }

            // Seed session history with only the persisted polygon. Selection
            // guidance points are created afterwards and must never become
            // independent undo/redo entries.
            draw.clearUndoRedoHistory();
            if (feature.id !== undefined) {
              coverageFeatureId = feature.id;
              draw.selectFeature(feature.id);
            }

            const bounds = new window.google.maps.LatLngBounds();
            initialCoordinatesRef.current.forEach((coordinate) =>
              bounds.extend(coordinate),
            );
            map.fitBounds(bounds, {
              bottom: 64,
              left: 64,
              right: 64,
              top: 64,
            });
            cameraListener = window.google.maps.event.addListenerOnce(
              map,
              "idle",
              () => {
                const fittedZoom = map.getZoom();
                if (typeof fittedZoom === "number" && fittedZoom > 0) {
                  map.setZoom(fittedZoom - 1);
                }
              },
            );
            refreshHistory();
            setStatus("editing");
          };

          const handleReady = () => loadInitialFeature();
          const handleFinish = (id: string | number) => {
            if (!draw) return;
            coverageFeatureId = id;
            const coordinates = getFeatureCoordinates(id);
            if (!coordinates) return;

            // Selection vertices and midpoints are Terra Draw guidance features.
            // Removing every feature except the polygon makes its edit handles
            // disappear after the first drag or midpoint insertion.
            draw.selectFeature(id);
            setStatus("editing");
            onPolygonComplete(coordinates);
            refreshHistory();
          };

          const handleChange = (ids: Array<string | number>, type: string) => {
            if (type !== "update") return;
            const coverageId = ids.find((id) => draw?.getSnapshotFeature(id));
            if (coverageId === undefined) return;
            const coordinates = getFeatureCoordinates(coverageId);
            if (!coordinates) return;
            setStatus("editing");
            onPolygonChange(coordinates);
            refreshHistory();
          };

          draw.on("ready", handleReady);
          draw.on("finish", handleFinish);
          draw.on("change", handleChange);
          draw.start();

          map.setOptions({ disableDoubleClickZoom: true });
          doubleClickListener = window.google.maps.event.addListener(
            map,
            "dblclick",
            (event: google.maps.MapMouseEvent) => {
              if (!draw || coverageFeatureId === undefined || !event.latLng) {
                return;
              }

              const feature = draw.getSnapshotFeature(coverageFeatureId);
              const coordinates = terraDrawPolygonToLatLng(feature);
              if (!coordinates) return;

              const zoom = map.getZoom() ?? 16;
              const target = event.latLng.toJSON();
              const metersPerPixel =
                (156543.03392 * Math.cos((target.lat * Math.PI) / 180)) /
                2 ** zoom;
              const updatedCoordinates = removePolygonVertexNear(
                coordinates,
                target,
                metersPerPixel * 18,
              );
              if (!updatedCoordinates) return;

              const updatedFeature = latLngToTerraDrawPolygon(
                updatedCoordinates,
                coverageFeatureId,
              );
              if (!updatedFeature) return;

              draw.updateFeatureGeometry(
                coverageFeatureId,
                updatedFeature.geometry,
              );
              draw.selectFeature(coverageFeatureId);
              refreshHistory();
            },
          );
          drawRef.current = draw;
        } catch (error) {
          console.error("No se pudo inicializar Terra Draw:", error);
          draw = null;
          drawRef.current = null;
          setStatus("error");
        }
      });
    };

    if (map.getProjection()) {
      initializeDraw();
    } else {
      projectionListener = window.google.maps.event.addListenerOnce(
        map,
        "projection_changed",
        initializeDraw,
      );
    }

    return () => {
      disposed = true;
      projectionListener?.remove();
      cameraListener?.remove();
      doubleClickListener?.remove();
      map.setOptions({ disableDoubleClickZoom: false });
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      draw?.stop();
      drawRef.current = null;
      setCanUndo(false);
      setCanRedo(false);
    };
  }, [enabled, initialCoordinates, map, onPolygonChange, onPolygonComplete]);

  if (!enabled) return null;

  const startDrawing = () => {
    const draw = drawRef.current;
    if (!draw || status === "loading") return;

    draw.clear();
    draw.clearUndoRedoHistory();
    setCanUndo(false);
    setCanRedo(false);
    draw.setMode("polygon");
    setStatus("drawing");
    onStart();
  };

  const cancelDrawing = () => {
    const draw = drawRef.current;
    if (!draw || status === "loading") return;

    draw.clear();
    const originalFeature = latLngToTerraDrawPolygon(
      initialCoordinatesRef.current,
      draw.getFeatureId(),
    );
    if (originalFeature) {
      draw.addFeatures([originalFeature]);
      draw.clearUndoRedoHistory();
      if (originalFeature.id !== undefined) {
        draw.selectFeature(originalFeature.id);
      }
      setStatus("editing");
    } else {
      draw.clearUndoRedoHistory();
      setStatus("ready");
    }
    setCanUndo(false);
    setCanRedo(false);
    onCancel();
  };

  const undo = () => {
    const draw = drawRef.current;
    if (!draw || !draw.undo()) return;
    synchronizeAfterHistoryChange(draw);
    setCanUndo(draw.canUndo());
    setCanRedo(draw.canRedo());
  };

  const redo = () => {
    const draw = drawRef.current;
    if (!draw || !draw.redo()) return;
    synchronizeAfterHistoryChange(draw);
    setCanUndo(draw.canUndo());
    setCanRedo(draw.canRedo());
  };

  const synchronizeAfterHistoryChange = (draw: TerraDraw) => {
    const polygon = draw
      .getSnapshot()
      .find(
        (feature) =>
          feature.geometry.type === "Polygon" &&
          feature.properties.mode === "polygon",
      );
    if (polygon?.id === undefined) return;

    draw.deselectFeature(polygon.id);
    const staleGuidanceIds = draw
      .getSnapshot()
      .filter(
        (feature) =>
          feature.geometry.type === "Point" &&
          feature.properties.mode === "select",
      )
      .map((feature) => feature.id)
      .filter((id): id is string | number => id !== undefined);
    if (staleGuidanceIds.length > 0) {
      draw.removeFeatures(staleGuidanceIds);
    }
    draw.selectFeature(polygon.id);

    const coordinates = terraDrawPolygonToLatLng(polygon);
    if (coordinates) {
      onPolygonChange(coordinates);
      setStatus("editing");
    }
  };

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          className="min-h-9 whitespace-nowrap rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300"
          disabled={
            status === "loading" || status === "drawing" || status === "error"
          }
          onClick={startDrawing}
          type="button"
        >
          {status === "editing" || status === "finished"
            ? "Dibujar nueva zona"
            : "Agregar polígono"}
        </button>
        <button
          className="min-h-9 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-300"
          disabled={status !== "drawing"}
          onClick={cancelDrawing}
          type="button"
        >
          Cancelar dibujo
        </button>
        <button
          className="min-h-9 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-300"
          disabled={!canUndo}
          onClick={undo}
          type="button"
        >
          Deshacer
        </button>
        <button
          className="min-h-9 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-300"
          disabled={!canRedo}
          onClick={redo}
          type="button"
        >
          Rehacer
        </button>
      </div>
      <span className="text-xs leading-5 text-gray-600" aria-live="polite">
        {status === "loading" && "Preparando herramienta..."}
        {status === "ready" && "Lista para dibujar"}
        {status === "drawing" && "Cierra la figura sobre el primer punto"}
        {status === "editing" &&
          "Arrastra un vértice verde para moverlo, un punto blanco para agregarlo o haz doble clic en un vértice para eliminarlo"}
        {status === "finished" && "Zona finalizada; recuerda guardar"}
        {status === "error" && "No se pudo activar la herramienta de dibujo"}
      </span>
    </div>
  );
}
